<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
import { captureError } from '@/composables/Sentry';
import type { Component } from 'vue';
import { computed, defineAsyncComponent, h } from 'vue';

const props = defineProps({
  iconName: {
    type: String,
    required: true,
  },
  iconFolder: {
    type: String,
    required: true,
  },
  iconSize: {
    type: String,
    default: '',
  },
});

const icons = import.meta.glob<{ default: Component }>('/src/assets/icons/svg/**/*.svg');

const iconPath = computed(() => `/src/assets/icons/svg/${props.iconFolder}/${props.iconName}.svg`);

const AsyncComp = computed(() => {
  const loadComponent = icons[iconPath.value];

  // If icon doesn't exist, return a fallback component immediately
  // This avoids Promise rejections and provides better UX
  if (!loadComponent) {
    console.warn(`Icon not found: ${iconPath.value}`);

    captureError(new Error(`Icon not found: ${iconPath.value}`), {
      message: `Icon not found: ${iconPath.value}`,
    });

    return {
      render: () => h('q-icon', { name: 'sym_o_error', size: props.iconSize }),
    };
  }

  return defineAsyncComponent({
    // Main loader function - attempts to import the SVG component
    loader: () =>
      loadComponent().then((module) => {
        if (!module?.default) {
          throw new Error(`Invalid module for icon: ${iconPath.value}`);
        }
        return module.default;
      }),

    // Fallback component shown when loading fails after all retry attempts
    errorComponent: {
      render: () => h('q-icon', { name: 'sym_o_error', size: props.iconSize }),
    },

    // Loading indicator shown while the icon is being imported
    loadingComponent: {
      render: () => h('q-icon', { name: 'o_more_horiz', size: props.iconSize }),
    },

    // Delay before showing loading component (prevents flash for quick loads)
    delay: 200,

    // Maximum time to wait before giving up and showing error component
    timeout: 3000,

    // Retry logic for handling temporary loading failures
    // Allows up to 2 retry attempts before permanently failing
    onError: (error, retry, fail, attempts) => {
      console.warn(`Error loading icon: ${iconPath.value}. Attempts: ${attempts}`, error);

      // Retry up to 2 times for transient network/loading issues
      if (attempts <= 2) {
        retry();
      } else {
        // After 2 failed attempts, give up and show error component
        fail();
      }
    },
  });
});
</script>

<template>
  <div class="icon-wrapper">
    <component :is="AsyncComp" :style="{ '--icon-size': iconSize }" />
    <slot name="tooltip" />
    <slot name="icon-tooltip" />
  </div>
</template>

<style scoped>
.icon-wrapper {
  display: inline-flex;
  align-items: center;
}

svg {
  display: block;
  width: var(--icon-size);
  height: var(--icon-size);
  flex-shrink: 0;
}
</style>
